import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CardHead } from '../ui/index'
import { modalClose } from '../../store/openModal/OpenModalSlices'
import { useAuth } from '../../context'

const API_BASE_URL = 'https://us-central1-gestordecontenido-8915e.cloudfunctions.net/publicSpecsTableJson'

export const ProductDetail = ({ closeSection }) => {
    const dispatch = useDispatch()
    const productInfo = useSelector(state => state.productInfo)
    const { user, loading: authLoading } = useAuth()
    const [productSpecs, setProductSpecs] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [expandedImage, setExpandedImage] = useState(null)
    const [loadedImages, setLoadedImages] = useState({})
    const [currentImageLoaded, setCurrentImageLoaded] = useState(false)

    const productImages = require.context('../../assets/products', true)
    const pdfRefProd = useRef(null)

    const imgName = (strName) => {
        let img

        try {
            img = productImages(`./${strName}.png`)
        }
        catch {
            img = productImages('./no-disponible.png')
        }

        return img
    }

    const closeModal = () => {
        dispatch(modalClose())

        if (closeSection) {
            closeSection()
        }
    }

    const formatLabel = (value) => {
        if (!value) return '-'

        return String(value)
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase())
    }

    const isDisplayableValue = (value) => {
        if (value === undefined || value === null || value === '') {
            return false
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase()
            return normalized !== 'n/a' && normalized !== 'na' && normalized !== '-'
        }

        if (Array.isArray(value)) {
            return value.some(isDisplayableValue)
        }

        if (typeof value === 'object') {
            return Object.values(value).some(isDisplayableValue)
        }

        return true
    }

    const isDisplayableEntry = (label, value) => {
        const normalizedLabel = String(label || '').trim().toLowerCase()

        if (normalizedLabel === 'isna' && value === false) {
            return false
        }

        return isDisplayableValue(value)
    }

    const formatDateValue = (value) => {
        if (!value) return '-'

        if (typeof value?.toDate === 'function') {
            return value.toDate().toLocaleDateString()
        }

        if (typeof value === 'object' && value._seconds) {
            return new Date(value._seconds * 1000).toLocaleDateString()
        }

        return stringifyValue(value)
    }

    const stringifyValue = (value) => {
        if (value === undefined || value === null || value === '') {
            return '-'
        }

        if (Array.isArray(value)) {
            return value.join(', ')
        }

        if (typeof value === 'object') {
            return JSON.stringify(value)
        }

        return String(value)
    }

    const renderStructuredValue = (value, keyPrefix) => {
        if (!isDisplayableValue(value)) {
            return '-'
        }

        if (Array.isArray(value)) {
            const primitiveItems = value.filter((item) => typeof item !== 'object' && isDisplayableValue(item))

            if (primitiveItems.length === value.length) {
                return primitiveItems.join(', ')
            }

            return (
                <div className='d-flex flex-column gap-2'>
                    {value
                        .filter((item) => isDisplayableValue(item))
                        .map((item, index) => (
                            <div key={`${keyPrefix}-${index}`} className='border rounded-2 p-2 bg-light-subtle'>
                                {typeof item === 'object'
                                    ? (
                                        <div className='d-flex flex-column gap-1'>
                                            {Object.entries(item)
                                                .filter(([nestedLabel, nestedValue]) => isDisplayableEntry(nestedLabel, nestedValue))
                                                .map(([nestedLabel, nestedValue]) => (
                                                    <div key={`${keyPrefix}-${index}-${nestedLabel}`}>
                                                        <span className='fw-semibold'>{formatLabel(nestedLabel)}:</span>{' '}
                                                        <span>{renderStructuredValue(nestedValue, `${keyPrefix}-${index}-${nestedLabel}`)}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )
                                    : stringifyValue(item)}
                            </div>
                        ))}
                </div>
            )
        }

        if (typeof value === 'object') {
            return (
                <div className='d-flex flex-column gap-1'>
                    {Object.entries(value)
                        .filter(([nestedLabel, nestedValue]) => isDisplayableEntry(nestedLabel, nestedValue))
                        .map(([nestedLabel, nestedValue]) => (
                            <div key={`${keyPrefix}-${nestedLabel}`}>
                                <span className='fw-semibold'>{formatLabel(nestedLabel)}:</span>{' '}
                                <span>{renderStructuredValue(nestedValue, `${keyPrefix}-${nestedLabel}`)}</span>
                            </div>
                        ))}
                </div>
            )
        }

        return stringifyValue(value)
    }

    const normalizeTableRows = (tableValue) => {
        if (Array.isArray(tableValue)) {
            const primitiveItems = tableValue.filter((item) => item !== undefined && item !== null && typeof item !== 'object' && isDisplayableValue(item))
            if (primitiveItems.length === tableValue.length) {
                return [{ type: 'list', items: primitiveItems }]
            }

            return tableValue
                .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
                .map((row) => ({
                    type: 'entries',
                    entries: Object.entries(row).filter(([label, value]) => isDisplayableEntry(label, value))
                }))
                .filter((row) => row.entries.length > 0)
        }

        if (tableValue && typeof tableValue === 'object') {
            const values = Object.values(tableValue)
            const nestedObjectRows = values.length > 0
                && values.every((value) => value && typeof value === 'object' && !Array.isArray(value))

            if (nestedObjectRows) {
                return values
                    .map((row) => ({
                        type: 'entries',
                        entries: Object.entries(row).filter(([label, value]) => isDisplayableEntry(label, value))
                    }))
                    .filter((row) => row.entries.length > 0)
            }

            const entries = Object.entries(tableValue)
                .filter(([label, value]) => isDisplayableEntry(label, value))

            return entries.length > 0 ? [{ type: 'entries', entries }] : []
        }

        return []
    }

    useEffect(() => {
        const controller = new AbortController()

        const fetchProductSpecs = async () => {
            if (!productInfo?.ItemCode) {
                setProductSpecs(null)
                setError('')
                return
            }
            if (authLoading) {
                return
            }
            if (!user) {
                setProductSpecs(null)
                setError('Sesion no autenticada. Volve a iniciar sesion.')
                return
            }

            setLoading(true)
            setError('')

            try {
                const sessionToken = await user.getIdToken(true)
                
                const response = await fetch(
                    `${API_BASE_URL}?sku=${encodeURIComponent(productInfo.ItemCode)}`,
                    {
                        method: 'GET',
                        signal: controller.signal,
                        headers: {
                            Authorization: `Bearer ${sessionToken}`
                        }
                    }
                )

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('No hay especificaciones publicas para este SKU.')
                    }

                    throw new Error('No se pudo obtener la informacion del producto.')
                }

                const data = await response.json()
                setProductSpecs(data)
            } catch (fetchError) {
                if (fetchError.name === 'AbortError') {
                    return
                }

                setProductSpecs(null)
                setError(fetchError.message || 'No se pudo cargar la informacion del producto.')
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false)
                }
            }
        }

        fetchProductSpecs()

        return () => {
            controller.abort()
        }
    }, [productInfo?.ItemCode, user?.uid, authLoading])

    const specTables = productSpecs?.tables && typeof productSpecs.tables === 'object'
        ? Object.entries(productSpecs.tables)
        : []
    const specFormTables = productSpecs?.specsForm && typeof productSpecs.specsForm === 'object'
        ? Object.entries(productSpecs.specsForm)
        : []
    const categoryPath = Array.isArray(productSpecs?.categoryPath) ? productSpecs.categoryPath.join(' / ') : '-'
    const productImagesFromApi = useMemo(() => (
        Array.isArray(productSpecs?.imagenes)
            ? productSpecs.imagenes.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim() !== '')
            : []
    ), [productSpecs?.imagenes])
    const imageGallery = useMemo(() => (
        productImagesFromApi.length > 0
            ? productImagesFromApi
            : [imgName(productInfo.ItemCode)]
    ), [productImagesFromApi, productInfo?.ItemCode])

    const showPrevImage = () => {
        setCurrentImageIndex((prev) => (
            prev === 0 ? imageGallery.length - 1 : prev - 1
        ))
    }

    const showNextImage = () => {
        setCurrentImageIndex((prev) => (
            prev === imageGallery.length - 1 ? 0 : prev + 1
        ))
    }

    useEffect(() => {
        setCurrentImageIndex(0)
    }, [productInfo?.ItemCode, productSpecs?.imagenes])

    useEffect(() => {
        setLoadedImages({})

        imageGallery.forEach((imageUrl) => {
            const img = new Image()
            img.src = imageUrl
            img.onload = () => {
                setLoadedImages((prev) => ({ ...prev, [imageUrl]: true }))
            }
            img.onerror = () => {
                setLoadedImages((prev) => ({ ...prev, [imageUrl]: false }))
            }
        })
    }, [imageGallery])

    useEffect(() => {
        const currentImage = imageGallery[currentImageIndex]
        setCurrentImageLoaded(loadedImages[currentImage] === true)
    }, [currentImageIndex, imageGallery, loadedImages])

    return (
        <div className="modalBase">
            {expandedImage && (
                <div className="modalBase" onClick={() => setExpandedImage(null)}>
                    <div
                        className="clrBgWhite p-3 rounded-3 shadow-lg"
                        style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className='d-flex justify-content-end pb-2'>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setExpandedImage(null)}
                            />
                        </div>
                        <img
                            src={expandedImage}
                            alt={`${productInfo.ItemName || productInfo.ItemCode || 'product'} enlarged`}
                            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                        />
                    </div>
                </div>
            )}
            <div ref={pdfRefProd} id='detalleProducto' className="clrBgWhite width50Modal mt-a maxH100 overflow-auto modalProd">
                <CardHead
                    section={'PRODUCTO'}
                    pdfRef={pdfRefProd}
                    pdfFileName={productInfo.ItemCode}
                    closeSection={closeModal}
                />

                <div className='overflowXscroll'>
                    <div className="border-bottom ms-3 p-4 px-5 px15MD">
                        <div className='d-flex mb-2 mb-3 flexColumnMD align-items-start'>
                            <div className='align-self-center' style={{ minWidth: '280px' }}>
                                <div className='border rounded-3 p-3 bg-light'>
                                    <div className='position-relative d-flex justify-content-center align-items-center'>
                                        {!currentImageLoaded && (
                                            <div
                                                className='position-absolute d-flex justify-content-center align-items-center text-muted'
                                                style={{ inset: 0, backgroundColor: 'rgba(255,255,255,0.75)' }}
                                            >
                                                Cargando imagen...
                                            </div>
                                        )}
                                        <img
                                            className="imgProducto align-self-center"
                                            src={imageGallery[currentImageIndex]}
                                            alt={`${productInfo.ItemName || productInfo.ItemCode || 'product'} ${currentImageIndex + 1}`}
                                            style={{ cursor: 'zoom-in' }}
                                            onLoad={() => setCurrentImageLoaded(true)}
                                            onError={() => setCurrentImageLoaded(false)}
                                            onClick={() => setExpandedImage(imageGallery[currentImageIndex])}
                                        />
                                        {imageGallery.length > 1 && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-light position-absolute start-0 top-50 translate-middle-y shadow-sm"
                                                    onClick={showPrevImage}
                                                >
                                                    ‹
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-light position-absolute end-0 top-50 translate-middle-y shadow-sm"
                                                    onClick={showNextImage}
                                                >
                                                    ›
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    <div className='d-flex justify-content-between align-items-center pt-3'>
                                        <span className='small text-muted'>
                                            {imageGallery.length > 1
                                                ? `${currentImageIndex + 1} / ${imageGallery.length}`
                                                : 'Imagen del producto'}
                                        </span>
                                        <button
                                            type="button"
                                            className='btn btn-sm btn-outline-secondary'
                                            onClick={() => setExpandedImage(imageGallery[currentImageIndex])}
                                            aria-label='Ver imagen ampliada'
                                            title='Ver imagen ampliada'
                                        >
                                            🔍+
                                        </button>
                                    </div>

                                    {imageGallery.length > 1 && (
                                        <div className='d-flex gap-2 pt-3 flex-wrap justify-content-center'>
                                            {imageGallery.map((imageUrl, index) => (
                                                <button
                                                    key={`${productInfo.ItemCode || 'product'}-thumb-${index}`}
                                                    type="button"
                                                    className={`btn p-1 border ${currentImageIndex === index ? 'border-dark' : 'border-light'}`}
                                                    onClick={() => {
                                                        setCurrentImageLoaded(loadedImages[imageUrl] === true)
                                                        setCurrentImageIndex(index)
                                                    }}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={`thumbnail ${index + 1}`}
                                                        style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className='textColorGreyM mb-0 p-3 fontSize15 lh-lg flex-grow-1'>
                                <div className='fontSize24 fw-bold mb-2'>
                                    {productInfo.ItemName || '-'}
                                </div>
                                <div className='d-flex flex-wrap gap-2 mb-3'>
                                    <span className='badge rounded-pill text-bg-light border'>SKU {productSpecs?.sku || productInfo.ItemCode || '-'}</span>
                                    <span className='badge rounded-pill text-bg-light border'>{productSpecs?.brand || '-'}</span>
                                    <span className='badge rounded-pill text-bg-light border'>{categoryPath}</span>
                                </div>
                                <div className='small text-muted'>
                                    Actualizado: {formatDateValue(productSpecs?.updatedAt)}
                                </div>
                                {!loading && !error && productSpecs?.manual && (
                                    <div className='pt-2'>
                                        <a className='btn btn-sm btn-outline-secondary' href={productSpecs.manual} target="_blank" rel="noreferrer">
                                            Descargar manual
                                        </a>
                                    </div>
                                )}
                                {loading && <div className='pt-2'>Cargando especificaciones del producto...</div>}
                                {!loading && error && <div className='pt-2'>{error}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="p-4 px-5 px15MD">
                        {!loading && !error && specTables.length === 0 && specFormTables.length === 0 && (
                            <p className='textColorGreyM mb-0'>
                                No hay tablas publicas disponibles para este producto.
                            </p>
                        )}

                        {!loading && !error && [...specTables, ...specFormTables].map(([tableTitle, tableValue]) => {
                            const rows = normalizeTableRows(tableValue)

                            if (rows.length === 0) {
                                return null
                            }

                            return (
                                <section key={tableTitle} className="mb-4 border rounded-3 overflow-hidden shadow-sm">
                                    <div className='px-4 py-3 bg-light border-bottom'>
                                        <h5 className='fw-bold mb-0'>{formatLabel(tableTitle)}</h5>
                                    </div>

                                    <div className='p-0'>
                                        {rows.some((row) => row.type === 'entries') && (
                                            <table className="table table-hover mb-0 tableProducts">
                                                <tbody>
                                                    {rows.map((row, rowIndex) => {
                                                        if (row.type !== 'entries') {
                                                            return null
                                                        }

                                                        return row.entries.map(([label, value]) => (
                                                            <tr key={`${tableTitle}-${rowIndex}-${label}`}>
                                                                <td scope="row" className='fw-semibold textColorGreyM w-50'>
                                                                    {formatLabel(label)}
                                                                </td>
                                                                <td className='fs-6'>
                                                                    {renderStructuredValue(value, `${tableTitle}-${rowIndex}-${label}`)}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    })}
                                                </tbody>
                                            </table>
                                        )}

                                        {rows.some((row) => row.type === 'list') && rows.map((row, rowIndex) => {
                                            if (row.type !== 'list') {
                                                return null
                                            }

                                            return (
                                                <ul key={`${tableTitle}-${rowIndex}-list`} className='list-group list-group-flush'>
                                                    {row.items.map((item, itemIndex) => (
                                                        <li key={`${tableTitle}-${rowIndex}-${itemIndex}`} className='list-group-item py-3'>
                                                            {stringifyValue(item)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )
                                        })}
                                    </div>
                                </section>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}
